import React, { useState, useContext, useMemo, useRef, useLayoutEffect, useEffect, useCallback } from 'react'
import { readTheme, saveTheme } from '../theme/frost'
import { useQuery, useMutation, useLazyQuery, useApolloClient } from '@apollo/client'
import UserContext from '../context/UserContext'
import languages, { getLanguageAcronym } from '../languages'
import useLearnerContext from '../hooks/useLearnerContext'
import { isOwnerRole } from '../util/roles'
import {
  GET_LANGUAGE_TREE_QUERY, SEED_CURRICULUM_MUTATION, COMPLETE_TOPIC_MUTATION,
  GET_MEETING_WITH_CONTENT_QUERY, ME_QUERY, PERSONAL_LESSON_QUERY,
} from '../query/gqlQueries'
import { streamOllamaGenerate } from '../ollama'
import StudentLessonView from './StudentLessonView'
import curricula from '../data/curricula'
import { getBuiltinLesson } from '../data/lessons'
import { parseObj } from '../lessons/buildTopicLesson'
import st from './LanguageTreeView.module.scss'
import { topicLessonFromServer, personalLessonFromServer } from '../lessons/serverLesson'

import CloseIcon from '@mui/icons-material/Close'
import LockIcon from '@mui/icons-material/Lock'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'

// Light / dark, shared with the lesson, home, reader and partner views through
// the same key. This view has no stylesheet of its own — it is inline styles
// plus one <style> block — so the palette lives here and is published to the
// CSS as custom properties on the root, which the <style> block reads.

// The iPhone/iPad app's look (theme/frost.css): the neutrals are the shared
// Frost colours, which already follow day and night; green stays for done.
const FROST = {
  bg: 'var(--frost-bg)', bgGrad: 'var(--frost-bg)', bgFade: 'var(--frost-scrim)',
  surface: 'var(--frost-surface)', surface2: 'var(--frost-card2)', closeBg: 'var(--frost-surface)',
  node: 'var(--frost-surface)', nodeLocked: 'var(--frost-card2)', doneTop: 'var(--frost-right-tint)', panel: 'var(--frost-panel)',
  border: 'var(--frost-border)', borderSoft: 'var(--frost-border)', borderDash: 'var(--frost-border2)', panelBorder: 'var(--frost-border)',
  divider: 'var(--frost-border)', link: 'var(--frost-border2)',
  text: 'var(--frost-text)', muted: 'var(--frost-muted)', blurb: 'var(--frost-muted)', dim: 'var(--frost-dim)',
  dimmer: 'var(--frost-dim)', dimmest: 'var(--frost-border2)', needs: 'var(--frost-dim)',
  accent: 'var(--frost-right)', accent2: 'var(--frost-action)', onAccent: 'var(--frost-on-action)',
  danger: 'var(--frost-wrong)', amber: 'var(--frost-amber)', overlay: 'var(--frost-scrim)',
}
const PALETTE = {
  dark: { ...FROST, accentRgb: '77,219,120', glowRgb: '77,219,120', brightRgb: '120,230,150' },
  light: { ...FROST, accentRgb: '47,125,69', glowRgb: '47,125,69', brightRgb: '47,125,69' },
}
// The custom properties the <style> block below reads, set on the root element.
const cssVars = (P) => ({
  '--lt-bg': P.bg, '--lt-bg-fade': P.bgFade, '--lt-node': P.node, '--lt-node-locked': P.nodeLocked,
  '--lt-done-top': P.doneTop, '--lt-border-soft': P.borderSoft, '--lt-divider': P.divider,
  '--lt-text': P.text, '--lt-blurb': P.blurb, '--lt-dim': P.dim, '--lt-dimmer': P.dimmer,
  '--lt-dimmest': P.dimmest, '--lt-needs': P.needs, '--lt-accent': P.accent, '--lt-accent2': P.accent2,
  '--lt-accent-rgb': P.accentRgb, '--lt-glow-rgb': P.glowRgb, '--lt-bright-rgb': P.brightRgb,
})

const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2']
const LEVEL_NAMES = { A1: 'Beginner', A2: 'Elementary', B1: 'Intermediate', B2: 'Upper Int.', C1: 'Advanced', C2: 'Mastery' }
const S = (v) => String(v || '').trim()

// ── the tree's layout: a tech tree read left to right ─────────────────────────
// Each level is a lane of columns; inside a level a topic sits one column after
// the topics of that level it needs, so a chain reads across, not down a list.
// A column holds at most `maxRows` cards (what fits the window's height); more
// spill into the next column. Each card sits near the cards it comes from, on
// one grid of rows, so a line that has to cross several columns can run along
// the gap between two rows instead of behind the cards.
const CARD_W = 236, CARD_H = 86, COL_GAP = 84, ROW_GAP = 18, LEVEL_GAP = 132
const PAD_X = 56, PAD_TOP = 64, PAD_BOTTOM = 22
const PITCH = CARD_H + ROW_GAP
export const rowsFor = (height) => Math.max(3, Math.min(9, Math.floor((height - PAD_TOP - PAD_BOTTOM + ROW_GAP) / PITCH)))

export function layoutTree(topics, maxRows) {
  const by = Object.fromEntries(topics.map(t => [t.slug, t]))
  const lvOf = (t) => t.level || 'A1'
  const memo = {}
  const depth = (t) => {
    if (memo[t.slug] != null) return memo[t.slug]
    memo[t.slug] = 0 // a loop in the data can't recurse forever
    const ps = (t.requires || []).map(r => by[r]).filter(p => p && p !== t && lvOf(p) === lvOf(t))
    return (memo[t.slug] = ps.length ? Math.max(...ps.map(depth)) + 1 : 0)
  }
  const tallest = Math.max(1, ...LEVELS.flatMap(lv => {
    const items = topics.filter(t => lvOf(t) === lv)
    const counts = {}
    items.forEach(t => { counts[depth(t)] = (counts[depth(t)] || 0) + 1 })
    return Object.values(counts)
  }))
  const rows = Math.min(maxRows, tallest)
  const inner = rows * CARD_H + (rows - 1) * ROW_GAP
  const pos = {}
  const bands = []
  let x = PAD_X
  LEVELS.forEach(lv => {
    const items = topics.filter(t => lvOf(t) === lv)
    if (!items.length) return
    const x0 = x
    const maxD = Math.max(...items.map(depth))
    for (let d = 0; d <= maxD; d++) {
      const group = items.filter(t => depth(t) === d).sort((a, b) => (a.order || 0) - (b.order || 0))
      for (let i = 0; i < group.length; i += rows) {
        const col = group.slice(i, i + rows).map(t => {
          const ys = (t.requires || []).map(r => pos[r]).filter(Boolean).map(p => p.y + CARD_H / 2)
          return { t, want: ys.length ? ys.reduce((a, b) => a + b, 0) / ys.length : null }
        })
        const known = col.filter(c => c.want != null)
        const mid = known.length ? known.reduce((a, c) => a + c.want, 0) / known.length : inner / 2
        col.forEach(c => { if (c.want == null) c.want = mid })
        col.sort((a, b) => a.want - b.want || (a.t.order || 0) - (b.t.order || 0))
        // Stack them in that order, then slide the stack to sit where they'd like, inside the lane.
        const tops = col.map((c, k) => k * PITCH)
        const shift = col.reduce((a, c, k) => a + (c.want - CARD_H / 2 - tops[k]), 0) / col.length
        const span = tops[tops.length - 1] + CARD_H
        const off = Math.max(0, Math.min(inner - span, Math.round(shift / PITCH) * PITCH))
        col.forEach((c, k) => { pos[c.t.slug] = { x, y: PAD_TOP + off + tops[k] } })
        x += CARD_W + COL_GAP
      }
    }
    bands.push({ lv, x0: x0 - LEVEL_GAP / 2 + 12, x1: x - COL_GAP + LEVEL_GAP / 2 - 12 })
    x += LEVEL_GAP - COL_GAP
  })
  const width = x - LEVEL_GAP + PAD_X
  const height = PAD_TOP + inner + PAD_BOTTOM
  const links = []
  topics.forEach(t => (t.requires || []).forEach(r => {
    const a = pos[r], b = pos[t.slug]
    if (!a || !b) return
    const x1 = a.x + CARD_W, y1 = a.y + CARD_H / 2, x2 = b.x, y2 = b.y + CARD_H / 2
    let d
    if (x2 - x1 <= LEVEL_GAP + 1) {
      // Next column: one soft S.
      const k = Math.max(36, (x2 - x1) * 0.5)
      d = `M ${x1} ${y1} C ${x1 + k} ${y1}, ${x2 - k} ${y2}, ${x2} ${y2}`
    } else {
      // Further: ease into the gap beside the card it goes to, run along it, ease in.
      const g = y2 >= y1 ? b.y - ROW_GAP / 2 : b.y + CARD_H + ROW_GAP / 2
      const e = COL_GAP * 0.8, h = e / 2
      d = `M ${x1} ${y1} C ${x1 + h} ${y1}, ${x1 + h} ${g}, ${x1 + e} ${g} H ${x2 - e} C ${x2 - h} ${g}, ${x2 - h} ${y2}, ${x2} ${y2}`
    }
    links.push({ key: `${r}>${t.slug}`, from: r, to: t.slug, d })
  }))
  return { pos, bands, links, width, height }
}
// initialLanguage / initialSlug: the dashboard opens the tree on a language and
// (optionally) straight onto one topic's modal.
export default function LanguageTreeView({ voiceSpeed, onClose, initialLanguage, initialSlug }) {
  const { username } = useContext(UserContext)
  const { learning } = useLearnerContext(username)
  const client = useApolloClient()
  const learningNames = (learning || []).map(l => l.name).filter(Boolean)

  const [langName, setLangName] = useState(initialLanguage || '')
  const effName = langName || learningNames[0] || 'Turkish'
  const code = getLanguageAcronym(effName) || 'tur'

  // errorPolicy 'all' (not 'ignore'): a failing query must surface, otherwise a
  // backend that hasn't shipped the Topic schema looks like an empty tree.
  const { data, loading, refetch, error: treeError } = useQuery(GET_LANGUAGE_TREE_QUERY, {
    variables: { langCode: code }, skip: !username, fetchPolicy: 'cache-and-network', errorPolicy: 'all',
  })
  const topics = data?.getLanguageTree || []
  const backendOutOfDate = !!treeError && /Cannot query field|Unknown type/i.test(treeError.message || '')
  // Only the owner may author / reseed the shared tree; students just climb it.
  const { data: meData } = useQuery(ME_QUERY, { errorPolicy: 'ignore' })
  const isOwner = isOwnerRole(meData?.me?.role)

  const [seedCurriculum] = useMutation(SEED_CURRICULUM_MUTATION)
  const [completeTopic] = useMutation(COMPLETE_TOPIC_MUTATION)
  const [fetchMeeting] = useLazyQuery(GET_MEETING_WITH_CONTENT_QUERY, { fetchPolicy: 'network-only' })

  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [selected, setSelected] = useState(null)   // topic node in the modal
  // Whether I already have my own version of the selected topic's lesson.
  const personalQ = useQuery(PERSONAL_LESSON_QUERY, { variables: { topicId: selected?.id }, skip: !selected, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore' })
  const [lesson, setLesson] = useState(null)       // { lesson, meetingId } → StudentLessonView

  // Opened from the dashboard on a specific topic: pop its modal once the tree
  // has loaded — once only, so closing the modal doesn't reopen it.
  const focusedSlugRef = useRef(null)
  useEffect(() => {
    if (!initialSlug || focusedSlugRef.current === initialSlug || !topics.length) return
    const t = topics.find(x => x.slug === initialSlug)
    if (t) { focusedSlugRef.current = initialSlug; setSelected(t) }
  }, [initialSlug, topics])

  const completedSlugs = useMemo(() => new Set(topics.filter(t => t.completed).map(t => t.slug)), [topics])
  const stateOf = (t) => t.completed ? 'done' : ((t.requires || []).every(r => completedSlugs.has(r)) ? 'open' : 'locked')
  const titleBySlug = useMemo(() => Object.fromEntries(topics.map(t => [t.slug, t.title])), [topics])

  // Hover a card → light up only its prerequisite chain and its unlocks, fade
  // the rest. Keeps a dense tree calm instead of drawing every edge at once.
  const [hovered, setHovered] = useState(null)
  const [theme, setTheme] = useState(readTheme)
  const P = PALETTE[theme] || PALETTE.dark
  const toggleTheme = () => setTheme(t => {
    const next = t === 'dark' ? 'light' : 'dark'
    saveTheme(next)
    return next
  })
  const dependents = useMemo(() => {
    const m = {}
    topics.forEach(t => (t.requires || []).forEach(r => { (m[r] = m[r] || []).push(t.slug) }))
    return m
  }, [topics])

  // ── the lanes: laid out to fit the window's height, scrolled sideways ───────
  const mainRef = useRef(null)
  // As state too, so the listeners come back when a lesson closes and the lanes return.
  const [mainEl, setMainEl] = useState(null)
  const setMain = useCallback((el) => { mainRef.current = el; setMainEl(el) }, [])
  const [maxRows, setMaxRows] = useState(null) // null until the window's been measured
  useLayoutEffect(() => {
    const el = mainRef.current
    if (!el) return
    const fit = () => setMaxRows(rowsFor(el.clientHeight))
    fit()
    const ro = new ResizeObserver(fit)
    ro.observe(el)
    return () => ro.disconnect()
  }, [mainEl, topics.length])
  const tree = useMemo(() => layoutTree(topics, maxRows || 6), [topics, maxRows])

  // Hovering a card lights up everything it still needs, and what it opens.
  const chainOf = useMemo(() => {
    if (!hovered) return null
    const by = Object.fromEntries(topics.map(t => [t.slug, t]))
    const set = new Set([hovered])
    const up = (slug) => (by[slug]?.requires || []).forEach(r => { if (!set.has(r)) { set.add(r); up(r) } })
    up(hovered)
    ;(dependents[hovered] || []).forEach(d => set.add(d))
    return set
  }, [hovered, topics, dependents])

  // The level in the middle of the window, for the rolling A1 / A2 / B1 at the top.
  const [levelAt, setLevelAt] = useState(null)
  const onScroll = useCallback(() => {
    const el = mainRef.current
    if (!el || !tree.bands.length) return
    const centre = el.scrollLeft + el.clientWidth / 2
    let band = tree.bands[0]
    tree.bands.forEach(b => { if (b.x0 <= centre) band = b })
    setLevelAt(band.lv)
  }, [tree])
  useEffect(onScroll, [onScroll, mainEl])
  const goToLevel = (lv) => {
    const b = tree.bands.find(x => x.lv === lv)
    if (b && mainRef.current) mainRef.current.scrollTo({ left: Math.max(0, b.x0 - 8), behavior: 'smooth' })
  }

  // Open on what's next: the first topic I can take, a third of the way in.
  const scrolledFor = useRef('')
  useEffect(() => {
    const el = mainRef.current
    if (!el || !topics.length || !maxRows || scrolledFor.current === code) return
    scrolledFor.current = code
    const next = topics.filter(t => stateOf(t) === 'open')
      .sort((a, b) => LEVELS.indexOf(a.level || 'A1') - LEVELS.indexOf(b.level || 'A1') || (a.order || 0) - (b.order || 0))[0]
    const p = next && tree.pos[next.slug]
    if (p) el.scrollLeft = Math.max(0, p.x - el.clientWidth / 3)
  }, [tree, code])

  // A mouse wheel moves along the lanes; a drag on the background pans, as on a map.
  useEffect(() => {
    const el = mainRef.current
    if (!el) return
    const wheel = (e) => {
      if (e.ctrlKey || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return
      if (el.scrollHeight > el.clientHeight + 1 && !e.shiftKey) return
      el.scrollLeft += e.deltaY
      e.preventDefault()
    }
    el.addEventListener('wheel', wheel, { passive: false })
    return () => el.removeEventListener('wheel', wheel)
  }, [mainEl, topics.length])
  const drag = useRef(null)
  const panStart = (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0 || e.target.closest('button')) return
    const el = mainRef.current
    drag.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop }
    el.setPointerCapture(e.pointerId)
  }
  const panMove = (e) => {
    const d = drag.current
    if (!d) return
    mainRef.current.scrollLeft = d.left - (e.clientX - d.x)
    mainRef.current.scrollTop = d.top - (e.clientY - d.y)
  }
  const panEnd = () => { drag.current = null }

  // ── AI-seed the curriculum for this language ─────────────────────────────────
  const generateCurriculum = async () => {
    setError('')
    // Prefer the hand-authored canonical tree (src/data/curricula.js); fall back
    // to the local model only for a language we haven't written one for.
    const builtIn = curricula[code]
    if (builtIn) {
      setBusy('Loading the curriculum…')
      try {
        await seedCurriculum({ variables: { langCode: code, name: builtIn.name || effName, topics: builtIn.topics } })
        await refetch()
      } catch (e) { setError(e.message || 'Could not load the curriculum.') }
      setBusy('')
      return
    }
    setBusy('Designing the tree…')
    const prompt = `Design a beginner→intermediate SKILL TREE for learning ${effName}, like a game tech tree. Produce 16-22 topics across CEFR A1, A2 and B1. Each topic is ONE concrete grammar or vocabulary unit a student unlocks in order.
For each: slug (kebab-case unique id), title (short), level ("A1"|"A2"|"B1"), order (1-based within its level), blurb (one short line), requires (array of slugs that MUST be done first — earlier topics only; the very first topics have []).
Prerequisites must reference ONLY slugs present in your list and form a sensible progression (basics before advanced).
Return ONLY minified JSON: {"topics":[{"slug":"","title":"","level":"","order":1,"blurb":"","requires":[]}]}`
    try {
      const raw = await streamOllamaGenerate({
        body: { model: 'gemma4:26b', prompt, format: 'json', options: { temperature: 0.4, num_predict: 2600 } },
        onProgress: (acc) => setBusy(`Designing the tree… (${acc.length} chars)`),
      })
      const o = parseObj(raw)
      const list = (Array.isArray(o?.topics) ? o.topics : []).map(t => ({
        slug: S(t?.slug).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
        title: S(t?.title), level: LEVELS.includes(t?.level) ? t.level : 'A1',
        order: Number.isFinite(t?.order) ? t.order : 0, blurb: S(t?.blurb),
        requires: Array.isArray(t?.requires) ? t.requires.map(s => S(s).toLowerCase().replace(/[^a-z0-9]+/g, '-')) : [],
      })).filter(t => t.slug && t.title)
      const slugs = new Set(list.map(t => t.slug))
      list.forEach(t => { t.requires = t.requires.filter(r => slugs.has(r) && r !== t.slug) }) // drop dangling prereqs
      if (list.length === 0) { setError('The model returned no topics — try again.'); setBusy(''); return }
      await seedCurriculum({ variables: { langCode: code, name: effName, topics: list } })
      await refetch()
    } catch (e) {
      setError((e.message || '').match(/Failed to fetch|Load failed/) ? 'Connection to the Mac Studio failed.' : (e.message || 'Generation failed.'))
    }
    setBusy('')
  }

  const openSavedMeeting = async (meetingId, t, msg) => {
    setBusy(msg || 'Loading lesson…')
    try {
      const res = await fetchMeeting({ variables: { identity: meetingId } })
      const parsed = parseObj(res.data?.getMeetingWithContent?.meeting?.properties?.lessonData)
      // Loaded: not busy any more — else every card stays disabled when the lesson closes.
      if (parsed) { setSelected(null); setBusy(''); setLesson({ lesson: parsed, meetingId, topicId: t.id, completed: t.completed }); return true }
    } catch (e) { setError(e.message) }
    setBusy('')
    return false
  }

  const failed = (e, fallback) => setError((e.message || '').match(/Failed to fetch|Load failed/) ? 'Connection to the server failed.' : (e.message || fallback))

  // Open the topic's lesson — the one that fits your own language, from the
  // server (the hand-written one, or one written for your language pair the
  // first time it's needed). `force` (owner) rebuilds it in place.
  const openTopic = async (t, force = false) => {
    setError('')
    if (!force && t.lessonMeetingId) {
      if (await openSavedMeeting(t.lessonMeetingId, t)) return
      setError('This lesson has no content — an owner can regenerate it.'); setBusy(''); return
    }
    setBusy(getBuiltinLesson(code, t.slug) ? 'Loading the lesson…' : (force ? 'Rebuilding the shared lesson…' : 'Getting the lesson ready…'))
    try {
      const meetingId = await topicLessonFromServer(client, {
        topicId: t.id, fresh: force,
        onProgress: (secs) => setBusy(`Writing the lesson on the Mac Studio… ${secs}s`),
      })
      await refetch()
      if (await openSavedMeeting(meetingId, t)) return
    } catch (e) { failed(e, 'The lesson couldn’t be written.') }
    setBusy('')
  }

  // Your own version, written from your profile and kept for you alone (the
  // same one the app opens). fresh: write a new one.
  const openMyPersonal = async (t, fresh = false) => {
    setError(''); setBusy('Tailoring your lesson…')
    try {
      const meetingId = await personalLessonFromServer(client, {
        topicId: t.id, fresh,
        onProgress: (secs) => setBusy(`Tailoring your lesson on the Mac Studio… ${secs}s`),
      })
      personalQ.refetch()
      if (await openSavedMeeting(meetingId, t, 'Loading your version…')) return
    } catch (e) { failed(e, 'Your lesson couldn’t be written.') }
    setBusy('')
  }

  const markComplete = async (t) => {
    try { await completeTopic({ variables: { topicId: t.id } }); await refetch(); setSelected(null) }
    catch (e) { setError(e.message) }
  }

  // ── the opened lesson replaces the tree ──────────────────────────────────────
  if (lesson) {
    const finishTopic = async () => {
      if (lesson.topicId && !lesson.completed) { try { await completeTopic({ variables: { topicId: lesson.topicId } }) } catch (e) { /* non-fatal */ } }
      setLesson(null); setBusy(''); refetch()
    }
    return <StudentLessonView lesson={lesson.lesson} meetingId={lesson.meetingId} voiceSpeed={voiceSpeed}
      onComplete={lesson.topicId ? finishTopic : undefined} completed={lesson.completed}
      onClose={() => { setLesson(null); setBusy(''); refetch() }} />
  }

  const done = topics.filter(t => t.completed).length
  const shownLevels = tree.bands.map(b => b.lv)
  const current = levelAt || shownLevels[0] || 'A1'
  const li = LEVELS.indexOf(current)

  return (
    <div className={st.view} style={{ color: P.text, ...cssVars(P) }}>
      <header className={st.header}>
        <div className={st.titleBlock}>
          <div className={st.eyebrow}>Skill Tree</div>
          <div className={st.titleRow}>
            <h1>Your {effName} tree</h1>
            <select value={effName} onChange={e => setLangName(e.target.value)} className={st.select}>
              {(learningNames.length ? learningNames : Object.values(languages)).map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className={st.count}>{done} / {topics.length} done</div>
        </div>

        {shownLevels.length > 0 && (
          <div className={st.levelDial} aria-live="polite" aria-label={`${current} ${LEVEL_NAMES[current]}`}>
            <div className={st.odometer} aria-hidden>
              <span className={st.reel}><span style={{ transform: `translateY(${-Math.floor(li / 2)}em)` }}>{['A', 'B', 'C'].map(c => <i key={c}>{c}</i>)}</span></span>
              <span className={st.reel}><span style={{ transform: `translateY(${-(li % 2)}em)` }}>{['1', '2'].map(c => <i key={c}>{c}</i>)}</span></span>
            </div>
            <div className={st.levelName} aria-hidden>
              <span style={{ transform: `translateY(${-li * 1.4}em)` }}>{LEVELS.map(l => <i key={l}>{LEVEL_NAMES[l]}</i>)}</span>
            </div>
            <div className={st.ticks}>
              {shownLevels.map(lv => (
                <button key={lv} className={lv === current ? st.tickOn : st.tick} onClick={() => goToLevel(lv)} title={`${lv} · ${LEVEL_NAMES[lv]}`} aria-label={`Go to ${lv}`} />
              ))}
            </div>
          </div>
        )}

        <div className={st.corner}>
          <button onClick={toggleTheme} className={st.round} title={theme === 'dark' ? 'Light mode' : 'Dark mode'}>
            {theme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </button>
          <button onClick={onClose} className={st.round} title="Close"><CloseIcon /></button>
        </div>
      </header>

      {error && <div className={st.note} style={{ color: P.danger }}>{error}</div>}
      {busy && <div className={st.note} style={{ color: P.accent }}>{busy}</div>}

      <main ref={setMain} className={st.lanes} onScroll={onScroll}
        onPointerDown={panStart} onPointerMove={panMove} onPointerUp={panEnd} onPointerCancel={panEnd}>
        {loading && topics.length === 0 ? (
          <div style={{ color: P.muted, padding: '2rem' }}>Loading…</div>
        ) : backendOutOfDate ? (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', maxWidth: 520, margin: '0 auto' }}>
            <div style={{ fontSize: '2.4rem' }}>⚠️</div>
            <h2 style={{ margin: '0.6rem 0' }}>The server hasn't been updated yet</h2>
            <p style={{ color: P.muted }}>The GraphQL server is running a version without the Skill Tree schema. Redeploy / restart it, then reopen this view.</p>
            <code style={{ color: P.danger, fontSize: '0.8rem' }}>{treeError.message}</code>
          </div>
        ) : topics.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', maxWidth: 460, margin: '0 auto' }}>
            <div style={{ fontSize: '2.4rem' }}>🌱</div>
            <h2 style={{ margin: '0.6rem 0' }}>No tree yet for {effName}</h2>
            {isOwner ? (
              <>
                <p style={{ color: P.muted }}>{curricula[code]
                  ? 'Load the canonical curriculum — the shared tree every learner of this language climbs. You can refine it afterwards.'
                  : 'No hand-written tree for this language yet — generate a starter with the local model, then refine it.'}</p>
                <button onClick={generateCurriculum} disabled={!!busy} style={{ marginTop: '1rem', background: P.accent2, color: P.onAccent, border: 'none', borderRadius: 10, padding: '0.7rem 1.5rem', fontWeight: 600, cursor: 'pointer' }}>
                  {curricula[code] ? '✦ Load curriculum' : '✦ Generate with AI'}
                </button>
              </>
            ) : (
              <p style={{ color: P.muted }}>Your teacher hasn't set up this tree yet — check back soon.</p>
            )}
          </div>
        ) : (
          <div className={st.canvas} style={{ width: tree.width + (isOwner ? 220 : 0), height: tree.height }}>
            {tree.bands.map((b, i) => (
              <div key={b.lv} className={`${st.band} ${i % 2 ? st.bandAlt : ''} ${b.lv === current ? st.bandOn : ''}`} style={{ left: b.x0, width: b.x1 - b.x0 }}>
                <div className={st.bandLabel}><b>{b.lv}</b><span>{LEVEL_NAMES[b.lv]}</span></div>
              </div>
            ))}
            <svg className={st.links} width={tree.width} height={tree.height}>
              {tree.links.map(l => {
                const on = chainOf && chainOf.has(l.from) && chainOf.has(l.to)
                const from = completedSlugs.has(l.from)
                const cls = on ? st.linkOn : chainOf ? st.linkFaded : from ? st.linkDone : st.link
                return <path key={l.key} d={l.d} className={cls} />
              })}
            </svg>
            {topics.map(t => {
              const p = tree.pos[t.slug]
              if (!p) return null
              const state = stateOf(t)
              const faded = chainOf && !chainOf.has(t.slug)
              const needs = (t.requires || []).filter(r => !completedSlugs.has(r)).map(r => titleBySlug[r] || r)
              return (
                <button key={t.id} onClick={() => setSelected(t)} disabled={!!busy}
                  onMouseEnter={() => setHovered(t.slug)} onMouseLeave={() => setHovered(null)}
                  onFocus={() => setHovered(t.slug)} onBlur={() => setHovered(null)}
                  className={`${st.node} ${st[state]} ${faded ? st.faded : ''}`}
                  style={{ left: p.x, top: p.y, width: CARD_W, height: CARD_H }}
                  title={state === 'locked' && needs.length ? `Needs: ${needs.join(' · ')}` : t.blurb || t.title}>
                  <span className={st.mark}>
                    {state === 'done' ? <CheckCircleIcon /> : state === 'locked' ? <LockIcon /> : <PlayArrowIcon />}
                  </span>
                  <span className={st.text}>
                    <span className={st.title}>{t.title}</span>
                    {t.blurb && <span className={st.blurb}>{t.blurb}</span>}
                  </span>
                  {t.lessonMeetingId && <span className={st.ready} title="Lesson ready" />}
                </button>
              )
            })}
            {isOwner && (
              <button onClick={generateCurriculum} disabled={!!busy} className={st.reload} style={{ left: tree.width, top: PAD_TOP }}
                title="Reload the canonical curriculum (upserts topics; keeps everyone's progress)">↻ Reload curriculum</button>
            )}
          </div>
        )}
      </main>

      {selected && (() => {
        const st = stateOf(selected)
        return (
          <div onClick={() => setSelected(null)} style={{ position: 'absolute', inset: 0, background: P.overlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: P.surface, border: `1px solid ${P.border}`, borderRadius: 16, padding: '1.4rem', maxWidth: 440, width: '90%', boxShadow: `0 20px 60px ${P.overlay}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ background: `rgba(${P.accentRgb},0.14)`, color: P.accent, borderRadius: 999, padding: '0.1rem 0.6rem', fontSize: '0.72rem', fontWeight: 600 }}>{selected.level}</span>
                <h2 style={{ margin: 0, fontSize: '1.25rem' }}>{selected.title}</h2>
              </div>
              {selected.blurb && <p style={{ color: P.muted, marginTop: '0.5rem' }}>{selected.blurb}</p>}

              {st === 'locked' ? (
                <div style={{ background: P.panel, border: `1px solid ${P.panelBorder}`, borderRadius: 10, padding: '0.8rem', marginTop: '0.8rem', color: P.muted }}>
                  🔒 Locked. Finish first: <strong style={{ color: P.text }}>{(selected.requires || []).filter(r => !completedSlugs.has(r)).map(r => titleBySlug[r] || r).join(', ')}</strong>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1.1rem', flexWrap: 'wrap' }}>
                  <button onClick={() => openTopic(selected)} disabled={!!busy} style={{ background: P.accent2, color: P.onAccent, border: 'none', borderRadius: 10, padding: '0.6rem 1.2rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                    <PlayArrowIcon fontSize="small" /> {selected.lessonMeetingId ? 'Open lesson' : 'Start lesson'}
                  </button>
                  <button onClick={() => openMyPersonal(selected)} disabled={!!busy} title="A private lesson tailored to your profile — only you see it" style={{ background: P.surface2, color: P.accent, border: `1px solid rgba(${P.accentRgb},0.4)`, borderRadius: 10, padding: '0.6rem 1.1rem', cursor: 'pointer' }}>
                    {personalQ.data?.personalLesson?.status === 'ready' ? '✦ Open my version' : '✦ Personalise for me'}
                  </button>
                  {isOwner && selected.lessonMeetingId && <button onClick={() => openTopic(selected, true)} disabled={!!busy} title="Owner: rebuild the shared lesson (replaces it for everyone)" style={{ background: P.surface2, color: P.muted, border: `1px solid ${P.border}`, borderRadius: 10, padding: '0.6rem 1.1rem', cursor: 'pointer' }}>↻ Regenerate</button>}
                  {!selected.completed && <button onClick={() => markComplete(selected)} disabled={!!busy} style={{ background: P.surface2, color: P.text, border: `1px solid ${P.border}`, borderRadius: 10, padding: '0.6rem 1.1rem', cursor: 'pointer' }}>Mark complete ✓</button>}
                  {selected.completed && <span style={{ color: P.accent, alignSelf: 'center' }}>✓ Completed</span>}
                  {getBuiltinLesson(code, selected.slug) && <span title="A hand-authored workbook lesson — opens instantly, no model needed" style={{ alignSelf: 'center', fontSize: '0.78rem', color: P.amber }}>✦ authored lesson</span>}
                </div>
              )}
              {busy && <div style={{ color: P.accent, marginTop: '0.8rem', fontSize: '0.85rem' }}>{busy}</div>}
            </div>
          </div>
        )
      })()}
    </div>
  )
}
